<script>
import DOMPurify from 'dompurify';
import moment from 'moment-timezone';
import {
  calendarEventToPayload,
  formatCalendarEventDates,
} from '@shared/utils/calendar';
import AddToCalendarDropdown from '@shared/components/AddToCalendarDropdown.vue';

const LOCATION_ICONS = {
  talkizy: 'video',
  visio: 'video',
  link: 'external-link-alt',
  address: 'map-marker-alt',
};

export default {
  components: { AddToCalendarDropdown },
  props: {
    event: {
      type: Object,
      required: true,
    },
  },
  computed: {
    isTrainingItem() {
      return this.event.type === 'training_item';
    },
    localDates() {
      return formatCalendarEventDates(this.event);
    },
    eventTzDates() {
      const tz = this.event.timezone || 'Europe/Paris';

      // Same timezone as the viewer: the local time line already says it all
      if (tz === moment.tz.guess()) return null;

      return formatCalendarEventDates(this.event, tz);
    },
    timezoneLabel() {
      const tz = this.event.timezone || 'Europe/Paris';
      const offset = moment.tz(tz).format('Z');
      return `${tz.replace(/_/g, ' ')} (UTC${offset})`;
    },
    sanitizedDescription() {
      return DOMPurify.sanitize(this.event.description || '');
    },
    locationIcon() {
      return LOCATION_ICONS[this.event.location_type] || 'map-marker-alt';
    },
    // Lien de visioconférence : on affiche un libellé plutôt que l'URL brute
    // (lien perso souvent long, avec token).
    isVisioLink() {
      return ['talkizy', 'visio'].includes(this.event.location_type);
    },
    calendarPayload() {
      return calendarEventToPayload(this.event);
    },
    locationUrl() {
      if (!this.event.location_value || this.event.location_type === 'address') {
        return null;
      }

      try {
        const parsed = new URL(this.event.location_value);
        return ['http:', 'https:'].includes(parsed.protocol) ? this.event.location_value : null;
      } catch (e) {
        return null;
      }
    },
    isPast() {
      if (!this.event.end_date) return false;
      return moment.utc(this.event.end_date).isBefore(moment.utc());
    },
  },
  methods: {
    goToTraining() {
      if (this.event.training_slug) {
        this.$router.push({
          name: 'mytraining',
          params: { slug: this.event.training_slug },
        });
        this.$emit('close');
      }
    },
  },
  i18n: {
    messages: {
      en: {
        event_detail: 'Event detail',
        training_item: 'Lesson',
        go_to_training: 'Go to training',
        add_to_calendar: 'Add to calendar',
        ics_other: 'Other (.ICS file)',
        close: 'Close',
        origin_time: 'Original time',
        unlocks_on: 'This content will be unlocked on {date}',
        about: 'About the event',
        visio_link: 'Link to your video conference',
      },
      fr: {
        event_detail: 'Détail de l\'événement',
        training_item: 'Leçon',
        go_to_training: 'Accéder à la formation',
        add_to_calendar: 'Ajouter au calendrier',
        ics_other: 'Autre (fichier .ICS)',
        close: 'Fermer',
        origin_time: 'Heure d\'origine',
        unlocks_on: 'Contenu débloqué le {date}',
        about: 'À propos de l\'événement',
        visio_link: 'Lien vers votre classe virtuelle',
      },
      es: {
        event_detail: 'Detalle del evento',
        training_item: 'Lección',
        go_to_training: 'Ir a la formación',
        add_to_calendar: 'Añadir al calendario',
        ics_other: 'Otro (archivo .ICS)',
        close: 'Cerrar',
        origin_time: 'Hora original',
        unlocks_on: 'Este contenido se desbloqueará el {date}',
        about: 'Acerca del evento',
        visio_link: 'Enlace a tu aula virtual',
      },
    },
  },
};
</script>

<template>
  <div class="modal-card" style="width: 560px">
    <header class="modal-card-head">
      <p class="modal-card-title">
        {{ isTrainingItem ? $t('training_item') : $t('event_detail') }}
      </p>
      <button type="button" class="delete" aria-label="close" @click="$emit('close')" />
    </header>

    <section class="modal-card-body">
      <div class="columns is-multiline">
        <div v-if="!isTrainingItem && event.picture" class="column is-5">
          <figure class="compcedm_cover">
            <img :src="event.picture" :alt="event.title" class="bradius-8">
          </figure>
        </div>

        <div class="column">
          <h2 class="title is-5 mb-3">
            {{ event.title }}
          </h2>

          <!-- Training item (drip) -->
          <template v-if="isTrainingItem">
            <div class="is-flex mb-2">
              <b-icon icon="unlock-alt" size="is-small" class="compcedm_rowicon mr-2" />
              <div>
                <p>{{ $t('unlocks_on', { date: localDates }) }}</p>
                <p v-if="eventTzDates" class="has-text-grey-dark is-size-8">
                  {{ $t('origin_time') }} : {{ eventTzDates }}
                </p>
              </div>
            </div>
            <p v-if="event.training_name" class="is-flex has-text-grey-dark mb-2">
              <b-icon icon="book" size="is-small" class="compcedm_rowicon mr-2" />
              <a href="#" @click.prevent="goToTraining">
                {{ event.training_name }}
              </a>
            </p>
          </template>

          <!-- Event -->
          <template v-else>
            <div class="is-flex mb-2">
              <b-icon icon="clock" size="is-small" class="compcedm_rowicon mr-2" />
              <p>{{ localDates }}</p>
            </div>

            <div class="is-flex has-text-grey-dark mb-2">
              <b-icon icon="globe" size="is-small" class="compcedm_rowicon mr-2" />
              <div>
                <p class="is-size-8">
                  {{ timezoneLabel }}
                </p>
                <p v-if="eventTzDates" class="is-size-8">
                  {{ $t('origin_time') }} : {{ eventTzDates }}
                </p>
              </div>
            </div>

            <p v-if="event.location_type" class="is-flex has-text-grey-dark mb-2">
              <b-icon :icon="locationIcon" size="is-small" class="compcedm_rowicon mr-2" />
              <a
                v-if="locationUrl"
                :href="locationUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="compcedm_meta"
              >{{ isVisioLink ? $t('visio_link') : event.location_value }}</a>
              <span v-else class="compcedm_meta">{{ event.location_value }}</span>
            </p>
          </template>
        </div>
      </div>

      <div v-if="!isTrainingItem && event.description" class="compcedm_about pt-4">
        <p class="has-text-grey-dark has-text-weight-medium is-size-8 is-uppercase mb-2">
          {{ $t('about') }}
        </p>
        <div class="content" v-html="sanitizedDescription" />
      </div>
    </section>

    <footer class="modal-card-foot is-justify-content-flex-end">
      <div class="buttons">
        <b-button type="is-text" @click="$emit('close')">
          {{ $t('close') }}
        </b-button>
        <AddToCalendarDropdown
          v-if="!isTrainingItem && !isPast"
          :calendar-event="calendarPayload"
          position="is-top-left"
        >
          <template #trigger>
            <b-tooltip :label="$t('add_to_calendar')" position="is-left">
              <b-button icon-left="calendar-plus" />
            </b-tooltip>
          </template>
        </AddToCalendarDropdown>
        <b-button
          v-else-if="event.training_slug"
          type="is-primary"
          icon-left="book"
          @click="goToTraining"
        >
          {{ $t('go_to_training') }}
        </b-button>
      </div>
    </footer>
  </div>
</template>

<style lang="scss" scoped>
.compcedm_cover {
  img {
    display: block;
    width: 100%;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    object-position: center;
  }
}

.compcedm_rowicon {
  flex-shrink: 0;
  margin-top: 2px;
}

.compcedm_meta {
  word-break: break-all;
}

.compcedm_about {
  border-top: 1px solid $theme_color_grey_light;
}
</style>
